<template>
  <div class="notification-templates-page">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <p class="text-muted mb-0">
        Сохранённые тексты для быстрой рассылки и автоматических уведомлений.
      </p>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        @click="openTemplateModal()"
      >
        <feather-icon
          icon="PlusIcon"
          size="16"
          class="mr-50"
        />
        Создать шаблон
      </b-button>
    </div>

    <vue-good-table
      :columns="templateColumns"
      :rows="templates"
      :pagination-options="{ enabled: true, perPage: 10 }"
      style-class="vgt-table striped"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'isActive'">
          <b-badge :variant="props.row.isActive ? 'light-success' : 'light-secondary'">
            {{ props.row.isActive ? 'Активен' : 'Отключён' }}
          </b-badge>
        </span>
        <span v-else-if="props.column.field === 'eventType'">
          {{ autoSendingLabel(props.row.templateKind) }}
        </span>
        <span v-else-if="props.column.field === 'action'">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50"
            @click="openTemplateModal(props.row)"
          >
            Изменить
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-50"
            @click="useTemplate(props.row)"
          >
            Использовать
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            @click="deactivateTemplate(props.row.id)"
          >
            Отключить
          </b-button>
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
      <span
        slot="emptystate"
        class="empty-table-title"
      >
        Шаблонов пока нет — создайте первый
      </span>
    </vue-good-table>

    <b-modal
      v-model="templateModalOpen"
      :title="editingTemplateId ? 'Редактировать шаблон' : 'Новый шаблон'"
      ok-title="Сохранить"
      cancel-title="Отмена"
      @ok="saveTemplate"
    >
      <b-form-group label="Название шаблона">
        <b-form-input
          v-model="templateForm.name"
          placeholder="Для внутреннего использования"
        />
      </b-form-group>
      <b-form-group label="Заголовок уведомления">
        <b-form-input v-model="templateForm.title" />
      </b-form-group>
      <b-form-group label="Текст уведомления">
        <b-form-textarea
          v-model="templateForm.text"
          rows="4"
        />
      </b-form-group>
      <b-form-group label="Тип шаблона (иконка в приложении)">
        <div class="d-flex align-items-center mb-50">
          <span class="template-kind-preview-icon mr-1">
            <feather-icon
              :icon="templateKindIcon(templateForm.templateKind)"
              size="20"
            />
          </span>
          <small class="text-muted">Так иконка будет выглядеть в приложении</small>
        </div>
        <b-form-select
          v-model="templateForm.templateKind"
          :options="templateKindOptions"
        />
        <small
          v-if="templateKindRequiresStart(templateForm.templateKind)"
          class="text-muted d-block"
        >
          При отправке уведомления этого типа нужно указать старт, к которому оно относится.
        </small>
        <small
          v-if="autoSendingDescription"
          class="text-success d-block mt-50"
        >
          {{ autoSendingDescription }}
        </small>
        <small
          v-else
          class="text-muted d-block mt-50"
        >
          Автоматическая отправка для этого типа не предусмотрена — только вручную из вкладки «Создать».
        </small>
      </b-form-group>
      <b-form-checkbox v-model="templateForm.isActive">
        Шаблон активен
      </b-form-checkbox>
    </b-modal>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import {
  BBadge,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue'
import { TEMPLATE_KIND_OPTIONS, templateKindAutoDescription, templateKindRequiresStart } from './notifications.constants'
import { showNotificationError, templateKindIcon } from './notifications.utils'

export default {
  name: 'NotificationTemplatesTab',
  components: {
    VueGoodTable,
    BBadge,
    BButton,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BModal,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      templates: [],
      templateModalOpen: false,
      editingTemplateId: null,
      templateForm: {
        name: '',
        title: '',
        text: '',
        templateKind: 'default',
        isActive: true,
      },
      templateKindOptions: TEMPLATE_KIND_OPTIONS,
      templateColumns: [
        { label: 'Название', field: 'name' },
        { label: 'Автоотправка', field: 'eventType' },
        { label: 'Статус', field: 'isActive' },
        { label: 'Действия', field: 'action', sortable: false },
      ],
    }
  },
  async mounted() {
    await this.loadTemplates()
  },
  computed: {
    autoSendingDescription() {
      return templateKindAutoDescription(this.templateForm.templateKind)
    },
  },
  methods: {
    templateKindIcon,
    templateKindRequiresStart,
    autoSendingLabel(kind) {
      return templateKindAutoDescription(kind) || '—'
    },
    async loadTemplates() {
      const { data } = await this.$http.get('notification-templates', { params: { maxResultCount: 100 } })
      this.templates = data.rows || []
    },
    useTemplate(template) {
      this.$emit('use', template)
    },
    openTemplateModal(template = null) {
      this.editingTemplateId = template?.id ?? null
      this.templateForm = template
        ? { ...template }
        : { name: '', title: '', text: '', templateKind: 'default', isActive: true }
      this.templateModalOpen = true
    },
    async saveTemplate(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        if (this.editingTemplateId) {
          await this.$http.patch(`notification-templates/${this.editingTemplateId}`, this.templateForm)
        } else {
          await this.$http.post('notification-templates', this.templateForm)
        }
        await this.loadTemplates()
        this.templateModalOpen = false
      } catch (e) {
        showNotificationError(this, e)
      }
    },
    async deactivateTemplate(id) {
      await this.$http.delete(`notification-templates/${id}`)
      await this.loadTemplates()
    },
  },
}
</script>

<style scoped>
.template-kind-preview-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #d9f5ff;
  color: #00baff;
}

.template-kind-preview-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
}
</style>
